'use client'

import React, { useMemo, useRef, useState } from 'react'
import { ArcElement, Chart as ChartJS, Legend, Tooltip, TooltipItem } from 'chart.js'
import { Pie } from 'react-chartjs-2'

import DynamicIcon from '@/components/DynamicIcon'
import { Kicker } from '@/components/Kicker'
import { Button } from '@/components/ui/button'
import { useWinsContext } from '@/providers/WinsProvider'
import { badgeColorMap, fullColorMapHSL } from '@/utilities/badgeColorMap'
import { kebabToCamel } from '@/utilities/toCamel'
import { cn } from '@/utilities/ui'

import type { IconName } from '@/components/DynamicIcon'

ChartJS.register(ArcElement, Tooltip, Legend)

type WinScopeChartColors = {
  background: string
  hover: string
}

const getWinScopeChartColors = (color: string): WinScopeChartColors => {
  if (color.includes('-dark')) {
    const baseColor = color.replace('-dark', '')
    return {
      background: `hsl(${fullColorMapHSL[baseColor][500]})`,
      hover: `hsl(${fullColorMapHSL[baseColor][400]})`,
    }
  }
  return {
    background: `hsl(${fullColorMapHSL[color][300]})`,
    hover: `hsl(${fullColorMapHSL[color][200]})`,
  }
}

/**
 * Pie chart of wins by jurisdiction scope for the wins data viz block.
 */
export const ChartWinScope = () => {
  const { filteredWins } = useWinsContext()
  const chartRef = useRef<ChartJS<'pie'> | null>(null)
  const [hiddenIndices, setHiddenIndices] = useState<Set<number>>(new Set())

  const wins = filteredWins.filter((w) => w.approved === true)

  const winRegionalPercentages = useMemo(() => {
    return [
      {
        title: 'State',
        count: wins.filter((w) => w.winScope === 'state').length,
        value: wins.filter((w) => w.winScope === 'state').length / wins.length || 0,
        color: 'tuscany',
      },
      {
        title: 'Federal',
        count: wins.filter((w) => w.winScope === 'national').length,
        value: wins.filter((w) => w.winScope === 'national').length / wins.length || 0,
        color: 'meltwater',
      },
    ]
  }, [wins])

  const pieData = {
    labels: winRegionalPercentages.map((winScope) => winScope.title),
    datasets: [
      {
        label: 'Wins by Topic',
        data: winRegionalPercentages.map((winScope) => winScope.value * 100),
        backgroundColor: winRegionalPercentages.map(
          (winScope) => getWinScopeChartColors(winScope.color).background,
        ),
        hoverBackgroundColor: winRegionalPercentages.map(
          (winScope) => getWinScopeChartColors(winScope.color).hover,
        ),
        borderWidth: 0,
        hoverOffset: 10,
        spacing: 0,
      },
    ],
    options: {
      layout: {
        padding: {
          top: 4,
          right: 4,
          bottom: 4,
          left: 4,
        },
      },
      width: 302,
      height: 302,
      borderRadius: '50%',
      borderWidth: 0,
      borderColor: 'hsl(0 0% 100%)',
      hoverBorderColor: 'hsl(0 0% 100%)',
      hoverBorderWidth: 2,
      hoverBorderRadius: '50%',
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (context: TooltipItem<'pie'>) => {
              const winScopeWinCount = winRegionalPercentages.find(
                (winScope) => winScope.title === context.label,
              )?.count
              return `${'\u00A0'}${context.parsed?.toFixed(0)}% [${winScopeWinCount} Win${winScopeWinCount && winScopeWinCount > 1 ? 's' : ''}]`
            },
          },
          caretSize: 6,
          padding: 16,
          borderWidth: 1,
          borderRadius: 16,
          borderColor: 'hsl(0 0% 19% / 0.2)',
          backgroundColor: 'hsl(0 0% 100%)',
          bodyColor: 'hsl(0 0% 19%)',
          titleColor: 'hsl(0 0% 19%)',
          titleMarginBottom: 8,
          titleFont: {
            family: 'Albert Sans, sans-serif',
            size: 16,
            weight: 600,
          },
          bodyFont: {
            family: 'Albert Sans, sans-serif',
            size: 16,
            weight: 400,
          },
        },
      },
    },
  }

  const toggleDataVisibility = (index: number) => {
    const chart = chartRef.current
    if (chart) {
      chart.toggleDataVisibility(index)
      chart.update()

      setHiddenIndices((prev) => {
        const next = new Set(prev)
        if (next.has(index)) {
          next.delete(index)
        } else {
          next.add(index)
        }
        return next
      })
    }
  }

  const areAllWinScopesZero =
    winRegionalPercentages.length === 0 ||
    winRegionalPercentages.every((winScope) => winScope.value === 0)

  return (
    <div className="flex flex-col rounded-xl bg-card p-4 lg:flex-1 lg:px-6 lg:pt-8 lg:pb-10">
      <Kicker tone="default" className="mb-4 w-fit">
        Wins Jurisdiction
      </Kicker>

      {areAllWinScopesZero ? (
        <div className="flex h-full items-center justify-center">
          <p className="text-sm text-muted-foreground">No wins found</p>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center gap-8">
          <div className="relative size-[220px] font-display text-foreground xl:size-[302px]">
            <Pie
              ref={chartRef as React.RefObject<ChartJS<'pie'>>}
              data={pieData}
              options={pieData.options}
            />
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {winRegionalPercentages.map((winScope, index) => {
              const isHidden = hiddenIndices.has(index)
              const isZero = winScope.value === 0
              const winScopeColor = kebabToCamel(winScope.color)
              const winScopeColorObject = badgeColorMap[winScopeColor]

              return (
                <Button
                  key={winScope.title}
                  ghost
                  icon={false}
                  onClick={() => toggleDataVisibility(index)}
                  className={cn(
                    'w-fit justify-start gap-2 border-none bg-transparent px-3 py-1 text-sm font-medium text-foreground transition-all duration-200 hover:border-none hover:bg-transparent',
                    isHidden || isZero ? 'line-through opacity-50 grayscale' : 'opacity-100',
                  )}
                  disabled={Boolean(isZero) ?? undefined}
                >
                  <span
                    className={cn(
                      'flex size-8 items-center justify-center rounded-xl',
                      winScopeColorObject.bg,
                      winScopeColorObject.text,
                    )}
                  >
                    <DynamicIcon name={'circle' as IconName} className="size-4" />
                  </span>
                  {winScope.title}
                  {`${'\u00A0\u00A0'}${((winScope.value ?? 0) * 100).toFixed(0)}%`}
                </Button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
